"use client";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";
import { customerOrdersService } from "~/connectors/orders.connector";
import type { Order, Pagination } from "~/types";

const DISMISSED_ORDERS_KEY = "dismissed-unpaid-orders";

interface UnpaidOrdersContextType {
  unpaidOrders: Pagination<Order> | null;
  dismissedOrders: Set<number>;
  visibleOrders: Order[];
  dismissAllOrders: () => void;
  refreshUnpaidOrders: () => Promise<void>;
  isLoading: boolean;
  hasUnpaidOrders: boolean;
  hasDismissedUnpaidOrders: boolean;
}

const UnpaidOrdersContext = createContext<UnpaidOrdersContextType | undefined>(
  undefined,
);

interface UnpaidOrdersProviderProps {
  children: ReactNode;
}

export function UnpaidOrdersProvider({ children }: UnpaidOrdersProviderProps) {
  const { status } = useSession();
  const isAuthenticated = status === "authenticated";
  const fetchGuardRef = useRef<boolean>(false);
  const [unpaidOrders, setUnpaidOrders] = useState<Pagination<Order> | null>(
    null,
  );
  const [dismissedOrders, setDismissedOrders] = useState<Set<number>>(() => {
    if (typeof window === "undefined") return new Set();
    const dismissed = localStorage.getItem(DISMISSED_ORDERS_KEY);
    return dismissed ? new Set(JSON.parse(dismissed)) : new Set();
  });
  const [isLoading, setIsLoading] = useState(false);

  const pathname = usePathname();
  const skipUnpaidCheck =
    pathname?.startsWith("/versuri") || pathname?.startsWith("/admin");

  const fetchUnpaidOrdersData = useCallback(async () => {
    setIsLoading(true);
    try {
      const orders = await customerOrdersService.getOrders({
        paymentStatus: "unpaid",
        status: "pending",
      });
      setUnpaidOrders(orders);
    } catch (error) {
      console.error("Failed to fetch unpaid orders:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || skipUnpaidCheck) {
      setUnpaidOrders(null);
      return;
    }

    if (fetchGuardRef.current) return;
    fetchGuardRef.current = true;
    fetchUnpaidOrdersData();
  }, [isAuthenticated, fetchUnpaidOrdersData, skipUnpaidCheck]);

  const refreshUnpaidOrders = useCallback(async () => {
    if (isLoading) return;
    fetchGuardRef.current = false;
    await fetchUnpaidOrdersData();
  }, [fetchUnpaidOrdersData, isLoading]);

  const dismissAllOrders = useCallback(() => {
    trackEvent(AnalyticsEvents.ORDER_UNPAID_POPUP_DISMISSED);
    const newDismissed = new Set(dismissedOrders);
    unpaidOrders?.data.forEach((order) => newDismissed.add(order.id));
    setDismissedOrders(newDismissed);
    localStorage.setItem(
      DISMISSED_ORDERS_KEY,
      JSON.stringify([...newDismissed]),
    );
  }, [dismissedOrders, unpaidOrders]);

  const visibleOrders =
    unpaidOrders?.data.filter((order) => !dismissedOrders.has(order.id)) || [];
  const hasUnpaidOrders = (unpaidOrders?.data?.length ?? 0) > 0;
  const hasDismissedUnpaidOrders =
    hasUnpaidOrders && visibleOrders.length === 0;

  return (
    <UnpaidOrdersContext.Provider
      value={{
        unpaidOrders,
        dismissedOrders,
        visibleOrders,
        dismissAllOrders,
        refreshUnpaidOrders,
        isLoading,
        hasUnpaidOrders,
        hasDismissedUnpaidOrders,
      }}
    >
      {children}
    </UnpaidOrdersContext.Provider>
  );
}

export function useUnpaidOrders() {
  const context = useContext(UnpaidOrdersContext);
  if (context === undefined) {
    throw new Error(
      "useUnpaidOrders must be used within an UnpaidOrdersProvider",
    );
  }
  return context;
}
